import React, { useContext } from 'react';
import classNames from 'classnames/bind';
import { CustomInitContext } from '@/components/CustomTravel/CustomInit';
import ServiceForm from '@/components/CustomTravel/ServiceForm';
import { CustomTravelEstimatePopupStep, CustomTravelParamType } from '@/components/CustomTravel/EstimatePopup';
import { DimmedContent, DimmedFooter, DimmedHeader } from '@/components/CustomTravel/DimmedPopup';
import InputNumberCounterField from '@/components/CustomTravel/ServiceFormFields/InputNumberCounterField';
import InputTextField from '@/components/CustomTravel/ServiceFormFields/InputTextField';
import CheckboxField from '@/components/CustomTravel/ServiceFormFields/CheckboxField';
import ConditionalField from '@/components/CustomTravel/ServiceFormFields/ConditionalField';
import RadioField from '@/components/CustomTravel/ServiceFormFields/RadioField';
import { CodeType } from '@/types/apollo';
import { CustomTravelPersonalCount, CustomTravelSpecialNote } from '@/atoms/customTravelFormState';
import usePopupHandler from '@/hooks/custom/usePopupHandler';
import SelectPassengerDisplay from './SelectPassengerDisplay';

import styles from './SelectPassengerForm.module.scss';

const cx = classNames.bind(styles);

export interface SelectPassengerFormValue {
  personalCount?: CustomTravelPersonalCount;
  specialNote?: CustomTravelSpecialNote;
}

interface SelectPassengerFormProps {
  externalValues?: { personalCount?: CustomTravelPersonalCount; specialNote?: CustomTravelSpecialNote };
  specialNotes: CodeType[];
}

const SelectPassengerForm: React.FC<SelectPassengerFormProps> = ({ externalValues, specialNotes }) => {
  const { handleBack, onSubmitNextStep, currentType, onSubmitInquiry } = usePopupHandler();
  const isAdvisorType = currentType === CustomTravelParamType.ADVISOR_TYPE;
  const { overseasProduct } = useContext(CustomInitContext);
  const minSeat = overseasProduct?.overseasBookMinSeat || 0;

  const handleSubmit = (_values: any) => {
    const selectedSpecialNote = specialNotes?.find?.(({ code }) => code === _values.specialNote.code);
    const { personalCount } = _values;

    if ((personalCount?.adult || 0) + (personalCount?.child || 0) < minSeat) {
      // TODO: check
      alert(`성인 또는 소아 인원은 ${minSeat}명 이상이어야 합니다.`);
      return;
    }
    const values = {
      ..._values,
      specialNote: {
        code: _values.specialNote.code,
        name: _values.specialNote.etcName || selectedSpecialNote?.name || '',
      },
    };
    if (isAdvisorType) {
      onSubmitInquiry(values);
    } else {
      onSubmitNextStep(values, CustomTravelEstimatePopupStep.SelectBudget);
    }
  };

  return (
    <ServiceForm onSubmit={handleSubmit} className={cx('SelectPassenger')} externalValues={externalValues}>
      <DimmedHeader>
        {!currentType && (
          <span className={cx('page')} data-event-nlog="imp" data-event-area-code="customtravel.popup.passenger">
            <span className={cx('blind')}>현재</span>
            <b>{'4'}</b>
            <span className={cx('blind')}>전체</span>
            <span>{'5'}</span>
          </span>
        )}
        <span className={cx('title')}>
          <span>여행 인원</span>
          <i>최소 {minSeat}명 이상 신청 가능합니다.</i>
        </span>
      </DimmedHeader>
      <DimmedContent>
        <div className={cx('passengers')}>
          <InputNumberCounterField
            name="personalCount.adult"
            label="성인"
            subLabel="만 12세 이상"
            defaultValue={1}
            min={1}
            max={999}
            nlogAreaCode="cus.selfadult"
          />
          <InputNumberCounterField
            name="personalCount.child"
            label="소아"
            subLabel="만 12세 미만"
            defaultValue={0}
            min={0}
            max={999}
            nlogAreaCode="cus.selfchild"
          />
          <InputNumberCounterField
            name="personalCount.infant"
            label="유아"
            subLabel="만 24개월 미만"
            defaultValue={0}
            min={0}
            max={999}
            nlogAreaCode="cus.selfinfant"
          />
        </div>
        <SelectPassengerDisplay />
        <CheckboxField
          name="personalCount.flexible"
          label="인원수가 변경될 수 있어요."
          className={cx('compromise')}
          defaultValue={false}
          nlogAreaCode="cus.selfperosnalflex"
        />
        <div className={cx('notice')}>
          <span>성인과 소아 여행 인원의 합이 최소 신청 인원 이상이어야 합니다.</span>
        </div>
        <div className={cx('radios')}>
          <span className={cx('subject')}>특이사항</span>
          {specialNotes.map(({ code, name, isDirectInput }, index) => (
            <React.Fragment key={code}>
              <RadioField
                name="specialNote.code"
                displayText={name}
                className={cx('radio')}
                value={code}
                defaultValue={index === 0 ? code : undefined}
                changeTrigger={({ setValue }) => {
                  setValue('specialNote.name', name, { shouldDirty: true, shouldValidate: true });
                }}
                nlogAreaCode={`cus.selfspecialnotes${code.replace('SN', '')}`}
              />
              {isDirectInput && (
                <div className={cx('etc')}>
                  <ConditionalField<string> targetFieldName="specialNote.code">
                    {(selectedCode) =>
                      selectedCode !== code ? (
                        <input placeholder="직접 작성하기" disabled />
                      ) : (
                        <InputTextField
                          required
                          name="specialNote.etcName"
                          type="text"
                          placeholder="직접 작성하기"
                          maxLength={50}
                        />
                      )
                    }
                  </ConditionalField>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </DimmedContent>
      <DimmedFooter>
        <div className={cx('progress')}>
          <button
            type="button"
            className={cx('prev')}
            onClick={handleBack(CustomTravelEstimatePopupStep.SelectDuration, { type: currentType })}
            data-event-nlog="click"
            data-event-area-code="cus.selfpersonalbackpage"
          >
            {currentType !== CustomTravelParamType.CHECK_ESTIMATE ? '이전' : '취소'}
          </button>
          <ConditionalField<Record<string, number>> targetFieldName="personalCount">
            {(personalCount) => (
              <button
                type="submit"
                className={cx('next')}
                data-event-nlog="click"
                data-event-area-code="cus.df"
                disabled={personalCount?.adult + personalCount?.child < minSeat}
              >
                {currentType !== CustomTravelParamType.CHECK_ESTIMATE ? '다음' : '수정하기'}
              </button>
            )}
          </ConditionalField>
        </div>
      </DimmedFooter>
    </ServiceForm>
  );
};

export default SelectPassengerForm;
